/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import * as yup from 'yup';
import { cloneDeep } from 'lodash';

import { FitinsurAPI, MessageCenter } from 'utils';

import { accessProfileHandler } from '../../accessProfile';
import { roleHandler } from '../../role';

function handleInitialAdjustments({
  questionnaire
}) {
  try {
    return questionnaire.map(question => {
      let answerQuestion = question.answerOptions?.find(ansOpt => ansOpt.idAnswerOption == question.idAnswerOption)

      if (answerQuestion && [38].includes(answerQuestion.idDomain) && question.idQuestionRoot) {
        questionnaire = questionnaire.map(qst => {
          if ([question.idQuestionRoot].includes(qst.idQuestion)) {
            qst.behaviorObject.isRefusedByHiddenQuestions = true;
          }
          return qst;
        })
      }

      if (question) {
        question.idQuestionnaireAnswers = null
        question.idQuestionnaireAnswersParent = null
        question.idQuestionnaireAnswersRoot = null

        if (question?.childrenQuestions?.length) {
          question.childrenQuestions = question.childrenQuestions.map(childQuestion => {
            childQuestion.idQuestionnaireAnswers = null
            childQuestion.idQuestionnaireAnswersParent = null
            childQuestion.idQuestionnaireAnswersRoot = null
            return childQuestion
          })
        }
      }

      return question;
    })
  } catch (e) {
    console.log("GENERAL - handleInitialAdjustments error", e)
  }
}

function groupMultipleFieldQuestions({
  mainIdQuestion,
  questionnaire,
  handleQuestionsInitialAdjustments
}) {
  const groupQuestions = [];

  const mainQuestions = questionnaire.filter(question => question.idQuestion === mainIdQuestion);
  for (const mainQuestion of mainQuestions) {
    let group = []

    const mainQuestionIndex = questionnaire.findIndex(question => question.idQuestion === mainIdQuestion);
    if (mainQuestionIndex || mainQuestionIndex == 0) group.push(questionnaire[mainQuestionIndex]);

    if (mainQuestionIndex != -1) questionnaire.splice(mainQuestionIndex, 1);

    if (mainQuestion.childrenQuestions) mainQuestion.childrenQuestions.map(childQuestion => group.push(childQuestion));

    group.sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

    if (handleQuestionsInitialAdjustments) group = handleQuestionsInitialAdjustments({ questionnaire: group });

    groupQuestions.push(group.map((question, index) => ({ ...question, key: null })));
  }

  return groupQuestions;
}

function sectionIsVisible({
  sectionName,
  questionnaire,
  permissions = {},
  currentModality,
  ignoreVisibilityByModality = false
}) {
  const permissionsValues = Object.values(permissions);

  return questionnaire
    .some(question => question?.behaviorObject?.sectionName === sectionName
      && question.visibleQuestion
      && (ignoreVisibilityByModality || !question.productPlans?.length || question.productPlans.includes(currentModality))
      && (
        !question?.behaviorObject?.idDomainAccessFilter ||
        !permissionsValues ||
        (!Array.isArray(question?.behaviorObject?.idDomainAccessFilter) ?
          permissionsValues.includes(question.behaviorObject.idDomainAccessFilter) :
          permissionsValues.find(v => v && question?.behaviorObject?.idDomainAccessFilter.includes(v))
        )
      )
    )
}

function createSectionRow({
  questions
}) {
  questions = JSON.parse(JSON.stringify(questions));
  return questions.map(question => {
    const { defaultValue } = question.behaviorObject || {};
    question.answer = defaultValue || "";
    question.answerJson = defaultValue || "";
    question.idAnswerOption = null;
    question.idPerson = null;
    question.idQuestionnaireAnswers = null;
    question.idQuestionnaireAnswersParent = null;
    question.idQuestionnaireAnswersRoot = null;
    question.key = null;

    if (question.behaviorObject?.extraExclamationWarning) {
      question.behaviorObject.extraExclamationWarning = false;
    }

    return question;
  })
}

function handleQuestionnaire({
  data,
  setQuestionnaire,
  setFieldValue,
  accessRoleData,
  rolesData,
  setRolesGroup,
  idAccessRole,
}) {
  let questionnaire = [];
  questionnaire = [
    ...data.persons,
    ...data.operations,
    ...data.payments,
    ...data.risks
  ];

  // 0 - GENERAL initial adjustments
  questionnaire = handleInitialAdjustments({ questionnaire })

  // 1 - ACCESS_PROFILE initial adjustments
  questionnaire = accessProfileHandler.events.handleInitialAdjustments({
    questionnaire,
    setFieldValue,
    accessRoleData,
    idAccessRole,
  });

  // 2 - ROLE initial adjustments
  const rolesGroupQuestions = groupMultipleFieldQuestions({
    mainIdQuestion: 1971,
    questionnaire,
    handleQuestionsInitialAdjustments: null
  });

  setRolesGroup(roleHandler.events.handleInitialAdjustments({
    questionnaire: rolesGroupQuestions,
    setFieldValue,
    globalQuestionnaire: questionnaire,
    accessRoleData,
    rolesData,
  }))

  questionnaire = questionnaire.sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

  setQuestionnaire(questionnaire);

  return questionnaire;
}

function handleSave({
  values,
  questionnaire,
  idAccessRole,
  idOperation,
  idOperationParent,
  SECTIONS,
  rolesGroup,
  isDraft,
  getDescription,
  sentUpdateAdminOperation,
  useUpdateAdminOperationRequest,
  mc,
  formikRef,
  history,
}) {

  buildValidation({
    questionnaire,
    idAccessRole,
    idOperation,
    idOperationParent,
    SECTIONS,
    rolesGroup,
    isDraft,
    getDescription,
    useUpdateAdminOperationRequest,
  }).validate(values, { abortEarly: false }).then(() => {
    const isValidSubmittedForm = handleValidationsSubmittedForm({ isDraft });

    if (isValidSubmittedForm) handleSubmit({
      values,
      questionnaire,
      idAccessRole,
      idOperation,
      idOperationParent,
      SECTIONS,
      rolesGroup,
      sentUpdateAdminOperation,
      useUpdateAdminOperationRequest,
      history,
      mc,
      getDescription
    });

  }).catch((error) => {
    onValidationError({
      error,
      mc,
      formikRef,
      questionnaire,
      getDescription
    })
  })
}

function buildValidation({
  questionnaire,
  idAccessRole,
  idOperation,
  idOperationParent,
  SECTIONS,
  rolesGroup,
  isDraft,
  getDescription,
  useUpdateAdminOperationRequest,
}) {
  const validationSchema = {};

  const validationQuestionnaire = handleSaveQuestionnaireAnswer({
    questionnaire,
    idAccessRole,
    idOperation,
    idOperationParent,
    onlyQuestions: true,
    SECTIONS,
    rolesGroup,
    useUpdateAdminOperationRequest,
  });

  let allQuestions = [...validationQuestionnaire];
  for (const question of allQuestions) {
    if (question.childrenQuestions?.length) allQuestions = [...allQuestions, ...question.childrenQuestions]
  }

  for (const question of allQuestions) {
    if (isDraft && ![].includes(question.idQuestion)) continue;
    if ([614].includes(question?.idDomainQuestion)) continue;
    if (!question?.key) continue;

    question?.childrenQuestions?.map(item => {
      if (item.visibleQuestion && item.fieldMask?.type == 'phone' && item.behaviorObject?.isRequired) {
        validationSchema[item.key] = yup.string().test({
          name: "answer-validation",
          message: getDescription("DOMAINS", 765),
          test: (value) => !!value && value.length >= 10
        })
      } else if (item.visibleQuestion && item.behaviorObject?.fieldMask == 'email') {
        if (item.answer) {
          validationSchema[item.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
        } else {
          if (item.behaviorObject?.isRequired) {
            validationSchema[item.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
          } else {
            validationSchema[item.key] = yup.string().notRequired();
          }
        }
      } else {
        validationSchema[item.key] = item.visibleQuestion && item.behaviorObject?.isRequired
          ? yup.string().test("answer-validation", getDescription("DOMAINS", 762), function (value) {
            return !!item.answer;
          }) : yup.string().notRequired();
      }
    })

    if (question.visibleQuestion && question.behaviorObject?.isRequired && question.fieldMask?.type == 'phone') {
      validationSchema[question.key] = yup.string().test({
        name: "answer-validation",
        message: getDescription("DOMAINS", 765),
        test: (value) => !!value && value.length >= 10
      })
    } else if (question.visibleQuestion && question.behaviorObject?.fieldMask == 'email') {
      if (question.answer) {
        validationSchema[question.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
      } else {
        if (question.behaviorObject?.isRequired) {
          validationSchema[question.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
        } else {
          validationSchema[question.key] = yup.string().notRequired();
        }
      }
    } else {
      validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired
        ? yup.string().test("answer-validation", getDescription("DOMAINS", 762), function (value) {
          return !!question.answer;
        }) : yup.string().notRequired();
    }
  }

  return yup.object().shape(validationSchema);
}

function handleValidationsSubmittedForm({
  isDraft
}) {
  if (isDraft) return true;

  let validationResult = true;

  return validationResult;
}

async function handleSubmit({
  values,
  questionnaire,
  idAccessRole,
  idOperation,
  idOperationParent,
  SECTIONS,
  rolesGroup,
  sentUpdateAdminOperation,
  useUpdateAdminOperationRequest,
  history,
  mc,
  getDescription
}) {
  if (sentUpdateAdminOperation.current) return;

  sentUpdateAdminOperation.current = true;

  handleSaveQuestionnaireAnswer({
    questionnaire,
    idAccessRole,
    idOperation,
    idOperationParent,
    onlyQuestions: false,
    SECTIONS,
    rolesGroup,
    useUpdateAdminOperationRequest,
    history,
    mc,
    getDescription
  });
}

function handleSaveQuestionnaireAnswer({
  questionnaire,
  idAccessRole,
  idOperation,
  idOperationParent,
  onlyQuestions,
  SECTIONS,
  rolesGroup,
  useUpdateAdminOperationRequest,
  history,
  mc,
  getDescription
}) {
  let questionnaireAnswers = cloneDeep(questionnaire);

  for (const [key, section] of Object.entries(SECTIONS)) {
    if (section.isMultiple) {
      let multipleFields = []
      switch (section.name) {
        case 'ROLE':
          multipleFields = cloneDeep(rolesGroup.reduce((acc, v) => acc.concat([...v.groups, v.groupRoot]), []));
          break;
        default:
          multipleFields = [];
          break;
      }

      multipleFields.map(group => {
        if (group?.length) {
          const rootQuestion = group.find(question => !question.idQuestionRoot);
          const childrenQuestions = group.filter(question => question.idQuestionRoot);
          rootQuestion.childrenQuestions = childrenQuestions;
          if (!onlyQuestions) {
            questionnaireAnswers = [...questionnaireAnswers, rootQuestion];
          } else {
            questionnaireAnswers = [...questionnaireAnswers, ...group];
          }
        }
      })
    }
  }

  let childrenQuestionIds = [];
  for (const question of questionnaireAnswers) {
    if (question) {
      if (
        (question.idDomainQuestion === 270 ||
          question.idDomainQuestion === 271 ||
          (question.idDomainQuestion === 44 && typeof question.answer === 'object')
        ) && question.answer
      ) {
        try {
          question.answer = JSON.stringify(question.answer)
        } catch (e) {
        }
      }

      if (SECTIONS[question.behaviorObject?.sectionName] && SECTIONS[question.behaviorObject?.sectionName].isMultiple) {
        if (question?.childrenQuestions?.length) {
          const idQuestions = question.childrenQuestions.map(question => question.idQuestion);
          childrenQuestionIds = [...childrenQuestionIds, ...idQuestions];
        }
      } else {
        question.childrenQuestions = [];
      }
    }
  }

  questionnaireAnswers = questionnaireAnswers.filter(v => !childrenQuestionIds.includes(v.idQuestion))

  if (onlyQuestions) return questionnaireAnswers;

  useUpdateAdminOperationRequest({
    idAccessRole,
    idOperation,
    idOperationParent,
    questionnaire: questionnaireAnswers,
  }).then((resp) => {
    if (resp.status === 200) {
      const { data: { value } } = resp;

      if (value?.redirectURL) {
        history.push(value.redirectURL);
        window.location.reload();
      } else {
        mc.success(getDescription("DOMAINS", 1005));
        history.push(`/management/58/access-role`);
      }
    }
  });
}

function onValidationError({
  error,
  mc,
  formikRef,
  questionnaire,
  getDescription
}) {
  questionnaire = cloneDeep(questionnaire);
  mc.error(getDescription("DOMAINS", 760))
  const allQuestions = questionnaire;

  let firstErrorKey = null;
  try {
    for (const question of allQuestions) {
      if (!formikRef.current.errors || !formikRef.current.errors[question.key]) continue;
      formikRef.current.setFieldError(question.key, null);
    }

    error.inner.map(({ message, path }) => {
      const question = allQuestions.find(question => question.key === path);
      if (question.dsQuestion !== "E-mail") mc.error(getDescription("DOMAINS", 764).replace('@@dsQuestion', question.dsQuestion));
      else mc.error(getDescription("DOMAINS", 761));
      if (!message.includes(["E-mail", "Telefone"])) formikRef.current.setFieldError(question?.key, `* ${getDescription("DOMAINS", 762)}`);
      else formikRef.current.setFieldError(question?.key, `* ${getDescription("DOMAINS", 763)}`);
      if (!firstErrorKey) firstErrorKey = question.key;
    })

    if (firstErrorKey) {
      const elements = global.window.document.getElementsByName(firstErrorKey);
      if (elements?.length) {
        setTimeout(() => elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' }), 300);
      }
    }
  } catch (error) {
    console.log('onValidationError catch', error)
  }
}

const events = {
  handleInitialAdjustments,
  handleQuestionnaire,
  sectionIsVisible,
  createSectionRow,
  handleSave,
}

export { events };